import { ActivatedRouteSnapshot, NavigationExtras, Route, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { PermissionGuardService } from '../services/permission-guard.service';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { SnackbarService } from '../services/snackbar.service';
import { PermissionMessageService } from '../services/permission-message.service';
import { ENUM_PERMISSIONS } from '../enums/permission.enum';

/**
 * Extras à passer à `router.navigateByUrl(url, SILENT_PERMISSION_DENIAL)`.
 * si le PermissionGuard refuse la route, la navigation est annulée.
 * La Promise résout à false.
 */
export const SILENT_PERMISSION_DENIAL: NavigationExtras = { state: { silentPermissionDenial: true } };

@Injectable({
  providedIn: 'root',
})
export class PermissionGuard {
  constructor(
    private permissionService: PermissionGuardService,
    private router: Router,
    private snackbarService: SnackbarService,
    private permissionMessageService: PermissionMessageService,
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean> | boolean {
    return this.permissionService.hasPermission(next).pipe(
      map(response => {
        if (!response) {
          // Refus silencieux demandé par le caller (cf. SILENT_PERMISSION_DENIAL) : on annule
          // simplement la navigation, la Promise de navigateByUrl résout à false.
          if (this.router.getCurrentNavigation()?.extras?.state?.silentPermissionDenial) {
            return false;
          }

          // PRED-3233 : l'onglet par défaut d'un écran de détail (route enfant '') est refusé, mais le
          // détail reste accessible dès qu'un autre onglet l'est → on ouvre le premier onglet autorisé.
          const fallbackTab = this.findFirstAccessibleSiblingTab(next);
          if (fallbackTab) {
            return this.router.parseUrl(`${state.url.split('?')[0].replace(/\/$/, '')}/${fallbackTab}`);
          }

          // Aucun onglet autorisé : refus du détail dans son ensemble. Le message reste volontairement
          // simple (pas de liste des droits d'onglet), la page forbidden n'affiche alors pas de code.
          const noTabAccessible: boolean = this.isDefaultTabRoute(next);
          const requiredPermission = noTabAccessible ? undefined : next.data?.permission;
          const hasIdParam: boolean = next.params?.id !== undefined;
          // Route de détail (`x/:id`), ou son onglet par défaut dont le refus vaut refus du détail.
          const isDetailRoute: boolean =
            next.routeConfig?.path?.endsWith(':id') ||
            (noTabAccessible && next.parent?.routeConfig?.path?.endsWith(':id'));
          const lastSegmentIsObjectId: boolean =
            next.url[next.url?.length - 1]?.path?.match(/^[0-9a-fA-F]{24}$/) !== null;

          // Détail refusé en cours de session : on reste sur la page courante avec un toast. En accès
          // direct par URL (`router.navigated` faux : aucune navigation aboutie), il n'y a pas de page
          // courante — annuler sans rediriger bloquerait l'app sur son écran de chargement initial,
          // donc on envoie vers /forbidden.
          if (hasIdParam && isDetailRoute && lastSegmentIsObjectId && this.router.navigated) {
            this.snackbarService.danger(this.permissionMessageService.buildAccessDeniedMessage(requiredPermission));
          } else {
            // La permission manquante est transmise à la page forbidden pour affichage.
            this.router.navigate(['/forbidden'], { state: { permission: requiredPermission } }).then();
          }

          return false;
        }

        // PRED-3233 : écran de liste à onglets (mat-tabs sans route) dont chaque onglet porte un code. Sans
        // aucun de ces codes, le composant n'aurait rien à afficher → refus avec la liste des droits d'onglet.
        const tabPermissions: ENUM_PERMISSIONS[] = next.data?.tabPermissions;
        if (tabPermissions?.length && !this.hasAnyTabPermission(tabPermissions)) {
          this.router.navigate(['/forbidden'], { state: { permission: tabPermissions } }).then();
          return false;
        }

        return true;
      }),
    );
  }

  private hasAnyTabPermission(tabPermissions: ENUM_PERMISSIONS[]): boolean {
    return this.permissionService.checkPermission(
      this.permissionService.authService.getCurrentUserPermissions(),
      tabPermissions,
    );
  }

  /** Vrai pour la route enfant '' (onglet par défaut) d'un écran de détail à onglets. */
  private isDefaultTabRoute(next: ActivatedRouteSnapshot): boolean {
    return next.routeConfig?.path === '' && (next.parent?.routeConfig?.children?.length ?? 0) > 1;
  }

  /** Routes frères gardées par PermissionGuard avec un code : ce sont les onglets soumis à permission. */
  private guardedSiblingTabs(next: ActivatedRouteSnapshot): Route[] {
    return (next.parent?.routeConfig?.children ?? []).filter(
      route =>
        route.path &&
        route.component &&
        !route.redirectTo &&
        !route.path.includes(':') &&
        route.canActivate?.some(guard => guard === PermissionGuard) &&
        route.data?.permission,
    );
  }

  /**
   * Premier onglet frère (dans l'ordre de déclaration des routes, qui suit l'ordre d'affichage) dont
   * l'utilisateur détient le code, ou null si aucun : le détail n'est alors pas accessible.
   */
  private findFirstAccessibleSiblingTab(next: ActivatedRouteSnapshot): string | null {
    if (!this.isDefaultTabRoute(next)) {
      return null;
    }
    const userPermissions = this.permissionService.authService.getCurrentUserPermissions();
    const accessible = this.guardedSiblingTabs(next).find(route =>
      this.permissionService.checkPermission(userPermissions, route.data.permission),
    );
    return accessible?.path ?? null;
  }
}
